import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { EventDTO } from '../../types';
import { fetchEventsForSchoolYear } from '../../api/events';
import { amountTone, formatDate, formatMoney } from '../../utils/format';
import { Icon } from '../../components/icons';
import { PaymentLegend, PaymentState, StatusBadge } from './StatusBadge';

interface PaymentDetail {
    amount: number;
    transactionDate: string;
    description: string;
}

interface EventStatus {
    eventId: number;
    eventName: string;
    paid: boolean;
    ignored: boolean;
    frozen: boolean;
}

interface PaymentSummary {
    childName: string;
    childId: number;
    totalAmount: number;
    totalReturns: number;
    payments: PaymentDetail[];
    eventStatuses: EventStatus[];
    saldo: number;
}

const stateOf = (status: EventStatus): PaymentState => (status.ignored ? 'ignored' : status.paid ? 'paid' : 'unpaid');

const PaymentSummaryTable: React.FC<{ refresh: boolean, startDate: string | null, endDate: string | null, schoolYear: string }> = ({ refresh, startDate, endDate, schoolYear }) => {
    const [summaryData, setSummaryData] = useState<PaymentSummary[]>([]);
    const [events, setEvents] = useState<string[]>([]);
    const [eventDetails, setEventDetails] = useState<EventDTO[]>([]);
    const [activeTooltip, setActiveTooltip] = useState<{childId: number, paymentIndex: number} | null>(null);
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

    useEffect(() => {
        const handleGlobalClick = () => {
            setActiveTooltip(null);
        };
        window.addEventListener('click', handleGlobalClick);
        return () => {
            window.removeEventListener('click', handleGlobalClick);
        };
    }, []);

    useEffect(() => {
        if (startDate && endDate) {
            let cancelled = false;
            const params = { startDate, endDate, schoolYear };
            axios.get<PaymentSummary[]>('/api/data/summary', { params })
                .then(response => {
                    if (cancelled) return;
                    setSummaryData(response.data);
                    setStatus('ready');
                    if (response.data.length > 0) {
                        const eventNames = response.data[0].eventStatuses.map(es => es.eventName);
                        setEvents(eventNames);

                        fetchEventsForSchoolYear(schoolYear)
                            .then(details => { if (details && !cancelled) setEventDetails(details); });
                    }
                })
                .catch(() => { if (!cancelled) setStatus('error'); });
            return () => { cancelled = true; };
        }
    }, [refresh, startDate, endDate, schoolYear]);

    const reloadSummary = useCallback(() => {
        const params = { startDate, endDate, schoolYear };
        return axios.get<PaymentSummary[]>('/api/data/summary', { params })
            .then(response => setSummaryData(response.data));
    }, [startDate, endDate, schoolYear]);

    const handleToggleIgnore = (childId: number, eventId: number) => {
        axios.post('/api/child-events/toggle', { childId, eventId })
            .then(() => reloadSummary());
    };

    const handleToggleFreeze = (eventId: number) => {
        const params = { startDate, endDate, schoolYear };
        axios.post(`/api/events/${eventId}/toggle-freeze`, null, { params })
            .then(() => {
                reloadSummary();
                // Also refresh event details to update the lock state
                fetchEventsForSchoolYear(schoolYear)
                    .then(details => { if (details) setEventDetails(details); });
            });
    };

    const totalIncome = summaryData.reduce((sum, row) => sum + row.totalAmount, 0);

    const paidCount = events.map(event => {
        const eventDetail = eventDetails.find(e => e.name === event);
        if (!eventDetail) return { eventName: event, count: 0 };

        const count = summaryData.filter(row => {
            const eventStatus = row.eventStatuses.find(es => es.eventId === eventDetail.id);
            return eventStatus && eventStatus.paid;
        }).length;

        return { eventName: event, count };
    });

    const sumOfPayments = events.map(event => {
        const eventDetail = eventDetails.find(e => e.name === event);
        if (!eventDetail) return { eventName: event, sum: 0 };

        const paidUsers = summaryData.filter(row => {
            const eventStatus = row.eventStatuses.find(es => es.eventId === eventDetail.id);
            return eventStatus && eventStatus.paid;
        }).length;

        return { eventName: event, sum: paidUsers * eventDetail.amount };
    });

    return (
        <section className="section">
            <h2 className="section-title">Podsumowanie płatności</h2>
            <div className="surface table-card">
                {status === 'loading' && (
                    <div className="loading-state"><span className="spinner-border spinner-border-sm" role="status" />Ładowanie podsumowania…</div>
                )}
                {status === 'error' && (
                    <div className="empty-state">Nie udało się pobrać podsumowania płatności. Odśwież stronę i spróbuj ponownie.</div>
                )}
                {status === 'ready' && summaryData.length === 0 && (
                    <div className="empty-state">Brak osób do wyświetlenia w tym roku szkolnym.</div>
                )}
                {status === 'ready' && summaryData.length > 0 && (
                    <>
                        <PaymentLegend note="Kliknij komórkę, aby zwolnić dziecko z wydarzenia lub cofnąć zwolnienie. Kłódka blokuje zmiany." />
                        <div className="table-scroll">
                            <table className="table data-table matrix">
                                <thead>
                                    <tr>
                                        <th className="col-lp" scope="col">Lp.</th>
                                        <th className="col-name" scope="col">Nazwa</th>
                                        {events.map(event => {
                                            const details = eventDetails.find(e => e.name === event);
                                            return (
                                                <th key={event} className="event-col" scope="col">
                                                    <div className="event-head">
                                                        <span className="event-name" title={event}>{event}</span>
                                                        {details && <span className="event-amount">{formatMoney(details.amount)}</span>}
                                                        {details && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); handleToggleFreeze(details.id); }}
                                                                className={details.frozen ? 'freeze-toggle frozen' : 'freeze-toggle'}
                                                                title={details.frozen ? 'Odblokuj wydarzenie (odmroź)' : 'Zablokuj wydarzenie (zamroź)'}
                                                                aria-label={`${details.frozen ? 'Odblokuj' : 'Zablokuj'} wydarzenie: ${event}`}
                                                                aria-pressed={details.frozen}
                                                            >
                                                                <Icon name={details.frozen ? 'lock' : 'unlock'} size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </th>
                                            );
                                        })}
                                        <th className="num" scope="col">Przychody</th>
                                        <th scope="col">Płatności</th>
                                        <th className="num" scope="col">Saldo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryData.map((row, index) => (
                                        <tr key={row.childId ?? index} className={amountTone(row.saldo) === 'negative' ? 'row-debt' : undefined}>
                                            <td className="col-lp">{index + 1}</td>
                                            <td className="col-name">{row.childName}</td>
                                            {row.eventStatuses.map(eventStatus => {
                                                const state = stateOf(eventStatus);
                                                const hint = eventStatus.frozen
                                                    ? 'Wydarzenie jest zablokowane — nie można zmieniać'
                                                    : eventStatus.ignored
                                                        ? 'Kliknij, aby przywrócić obowiązek płatności'
                                                        : 'Kliknij, aby zwolnić z płatności';
                                                return (
                                                    <td key={eventStatus.eventId} className="status-cell">
                                                        <StatusBadge
                                                            state={state}
                                                            onClick={() => handleToggleIgnore(row.childId, eventStatus.eventId)}
                                                            disabled={eventStatus.frozen}
                                                            title={`${eventStatus.eventName}: ${state === 'paid' ? 'wpłacono' : state === 'ignored' ? 'zwolniony' : 'brak wpłaty'}. ${hint}`}
                                                            ariaLabel={`${row.childName}, ${eventStatus.eventName}: ${state === 'paid' ? 'wpłacono' : state === 'ignored' ? 'zwolniony z płatności' : 'brak wpłaty'}`}
                                                        />
                                                    </td>
                                                );
                                            })}
                                            <td className="num">{formatMoney(row.totalAmount)}</td>
                                            <td className="payments-cell">
                                                <div className="payment-chips-wrapper">
                                                    {row.payments.map((p, i) => {
                                                        const isTooltipActive = activeTooltip && activeTooltip.childId === row.childId && activeTooltip.paymentIndex === i;
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={i}
                                                                className={`payment-chip${p.amount < 0 ? ' negative' : ''}`}
                                                                aria-expanded={!!isTooltipActive}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveTooltip(isTooltipActive ? null : { childId: row.childId, paymentIndex: i });
                                                                }}
                                                            >
                                                                {formatMoney(p.amount, { signed: true })}
                                                                {isTooltipActive && (
                                                                    <span className="custom-tooltip" role="tooltip">
                                                                        <span className="tooltip-date d-block">{formatDate(p.transactionDate)}</span>
                                                                        <span className="tooltip-desc d-block">{p.description}</span>
                                                                    </span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                            <td className={`num tone-${amountTone(row.saldo)}`}>{formatMoney(row.saldo, { signed: true })}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td className="col-lp"></td>
                                        <td className="col-name">Wpłacili</td>
                                        {paidCount.map(pc => (
                                            <td key={pc.eventName} className="center">
                                                {pc.count}<span className="count-of">/{summaryData.length}</span>
                                            </td>
                                        ))}
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr>
                                        <td className="col-lp"></td>
                                        <td className="col-name">Suma</td>
                                        {sumOfPayments.map(sp => (
                                            <td key={sp.eventName} className="center nowrap">{formatMoney(sp.sum)}</td>
                                        ))}
                                        <td className="num">{formatMoney(totalIncome)}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default PaymentSummaryTable;
